import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { publicRequest, privateRequest } from "../utils/api";

// Types based on backend models and API responses
export interface TeamMember {
  id: number;
  name: string;
  role: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TeamMembersResponse {
  team_members: TeamMember[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface TeamMemberResponse {
  id: string;
  name: string;
  role: string;
  image_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TeamMembersQueryParams {
  per_page?: number;
  page?: number;
}

// Query keys
export const TEAM_MEMBERS_QUERY_KEY = ["team-members"];
export const TEAM_MEMBER_QUERY_KEY = (id: number) => ["team-members", id];

// Fetch team members with pagination
const fetchTeamMembers = async (
  params: TeamMembersQueryParams = {}
): Promise<TeamMembersResponse> => {
  try {
    const response = await publicRequest.get("/team-members", { params });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching team members:", error);
    throw error;
  }
};

// Fetch single team member
const fetchTeamMember = async (id: number): Promise<TeamMemberResponse> => {
  try {
    const response = await publicRequest.get(`/team-members/${id}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching team member:", error);
    throw error;
  }
};

// Create team member (admin only)
const createTeamMember = async (teamMemberData: {
  name: string;
  role: string;
  image_url?: string;
}): Promise<{ team_member: TeamMemberResponse }> => {
  try {
    const response = await privateRequest.post("/team-members", teamMemberData);
    return response.data.data;
  } catch (error) {
    console.error("Error creating team member:", error);
    throw error;
  }
};

// Update team member (admin only)
const updateTeamMember = async ({
  id,
  teamMemberData,
}: {
  id: number;
  teamMemberData: {
    name?: string;
    role?: string;
    image_url?: string;
  };
}): Promise<{ team_member: TeamMemberResponse }> => {
  try {
    const response = await privateRequest.put(
      `/team-members/${id}`,
      teamMemberData
    );
    return response.data.data;
  } catch (error) {
    console.error("Error updating team member:", error);
    throw error;
  }
};

// Delete team member (admin only)
const deleteTeamMember = async (id: number): Promise<void> => {
  try {
    await privateRequest.delete(`/team-members/${id}`);
  } catch (error) {
    console.error("Error deleting team member:", error);
    throw error;
  }
};

// Custom hooks
export const useTeamMembers = (params: TeamMembersQueryParams = {}) => {
  return useQuery({
    queryKey: [...TEAM_MEMBERS_QUERY_KEY, params],
    queryFn: () => fetchTeamMembers(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useTeamMember = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: TEAM_MEMBER_QUERY_KEY(id),
    queryFn: () => fetchTeamMember(id),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useCreateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
    },
  });
};

export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
    },
  });
};

export const useDeleteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
    },
  });
};
